import { useCallback, useEffect, useRef } from 'react';

/**
 * Data-freshness plumbing for the operational-loop pages.
 *
 * Re-runs an existing fetch callback when the tab/window regains focus, and
 * returns a guarded `refresh()` to wire to a manual refresh button. No polling.
 *
 * Usage:
 *   const refresh = useFocusRefetch(fetchAll);
 *   ...
 *   <button onClick={refresh}>Refresh</button>
 *
 * Notes:
 * - The in-flight guard (`runningRef`) prevents overlapping focus/manual
 *   refetches from stacking — a second call is a no-op while one is running.
 * - It intentionally does NOT wrap the page's own mount/dep-driven fetch
 *   (`useEffect(() => { fetchAll(); }, [fetchAll])`). Leave that as-is so
 *   legitimately-different queries (e.g. OpsCalendar's weekStart change) are
 *   never dropped by the guard.
 * - `fetchFn` must be a stable callback (memoize with useCallback, as all five
 *   loop pages already do) — the focus listeners re-bind whenever it changes.
 */
export default function useFocusRefetch(fetchFn) {
  const runningRef = useRef(false);

  const refresh = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      await fetchFn();
    } finally {
      runningRef.current = false;
    }
  }, [fetchFn]);

  useEffect(() => {
    const onFocus = () => {
      // visibilitychange also fires on hide — only refetch when visible.
      if (document.visibilityState === 'hidden') return;
      refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);

  return refresh;
}
